import { Children, isValidElement, type ReactNode } from 'react';
import { cn } from '~/lib/utils';

/** Shared padded content column used under FeaturePageHeader. */
export const featurePageContentClassName =
  'mx-auto w-full max-w-6xl px-4 py-5 md:px-6 xl:max-w-none';

/**
 * Horizontal insets only (no vertical padding) — matches the reading column /
 * sticky header inner width. Use to keep chrome aligned when the vertical
 * rhythm is managed by a flex column (e.g. the reports tab's fixed toolbar).
 */
export const featurePageContentXClassName =
  'mx-auto w-full max-w-6xl px-4 md:px-6 xl:max-w-none';

/** Pulls out of featurePageContent* padding to the column’s border box. */
export const featurePageBleedClassName =
  '-mx-4 w-[calc(100%+2rem)] md:-mx-6 md:w-[calc(100%+3rem)]';

/** Rest-state gutters matching the reading column; they scroll away with wide content. */
export const featurePageBleedGutterClassName = 'w-4 shrink-0 md:w-6';

type FeaturePageBleedProps = React.ComponentPropsWithoutRef<'div'>;

/**
 * Full-bleed horizontal scrollport for tables inside a feature-page content
 * column. At rest, gutters keep the first column aligned with SectionHeading;
 * while scrolling, content clips edge-to-edge of the content column / shell.
 */
export function FeaturePageBleed({ children, className, ...props }: FeaturePageBleedProps) {
  return (
    <div
      className={cn('overflow-x-auto', featurePageBleedClassName, className)}
      {...props}
    >
      <div className="flex min-w-full">
        <div className={featurePageBleedGutterClassName} aria-hidden />
        <div className="min-w-0 flex-1">{children}</div>
        <div className={featurePageBleedGutterClassName} aria-hidden />
      </div>
    </div>
  );
}

export type FeaturePageShellProps = React.ComponentPropsWithoutRef<'div'>;

type FeaturePageChromeComponent = {
  chrome?: true | 'sticky';
};

function isFeaturePageChrome(child: ReactNode): boolean {
  if (!isValidElement(child)) return false;
  const chrome = (child.type as FeaturePageChromeComponent).chrome;
  if (chrome === true) return true;
  if (chrome === 'sticky') return Boolean((child.props as { sticky?: boolean }).sticky);
  return false;
}

/**
 * Canonical feature-page canvas matching Home / Overview chrome.
 * Header chrome stays outside the scrollport so the page scrollbar never
 * runs over FeaturePageHeader / sticky TitleBar.
 */
export default function FeaturePageShell({ children, className, ...props }: FeaturePageShellProps) {
  const childList = Children.toArray(children);
  const chrome = childList.filter(isFeaturePageChrome);
  const body = childList.filter((child) => !isFeaturePageChrome(child));

  return (
    <div
      className={cn(
        'bg-muted/40 absolute inset-0 flex flex-col overflow-hidden',
        className,
      )}
      {...props}
    >
      {chrome}
      {body.length > 0 ? (
        <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
          {body}
        </div>
      ) : null}
    </div>
  );
}
